import React, { useState } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiUser, FiGlobe, FiLock, FiLink, FiTrash2, FiSave } = FiIcons;

function SettingsPage() {
  const [settings, setSettings] = useState({
    name: 'Alex Doe',
    username: 'alexdoe',
    bio: 'Building tools for makers. Currently working on productivity apps and developer workflows.',
    defaultPrivacy: 'private',
    autoPublish: false,
    emailNotifications: true,
    weeklyDigest: true
  });

  const [integrations, setIntegrations] = useState([
    { name: 'GitHub', connected: true, lastSync: '2 hours ago' },
    { name: 'VS Code', connected: true, lastSync: '30 minutes ago' },
    { name: 'Figma', connected: false, lastSync: null },
    { name: 'Linear', connected: false, lastSync: null }
  ]);

  const handleSettingChange = (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  const handleSave = () => {
    // Save settings logic would go here
    console.log('Saving settings:', settings);
  };

  const handleDeleteAccount = () => {
    if (window.confirm('Are you sure you want to delete your account? This action cannot be undone.')) {
      // Delete account logic would go here
      console.log('Deleting account...');
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="min-h-screen py-8"
    >
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-12"
        >
          <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 dark:text-white mb-4">
            Settings
          </h1>
          <p className="text-lg text-gray-600 dark:text-gray-300">
            Manage your account and privacy preferences.
          </p>
        </motion.div>

        <div className="space-y-8">
          {/* Profile Section */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6"
          >
            <div className="flex items-center space-x-3 mb-6">
              <SafeIcon icon={FiUser} className="w-5 h-5 text-gray-600 dark:text-gray-300" />
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                Profile
              </h2>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Name
                </label>
                <input
                  type="text"
                  value={settings.name}
                  onChange={(e) => handleSettingChange('name', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-gray-500 focus:border-transparent"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Username
                </label>
                <input
                  type="text"
                  value={settings.username}
                  onChange={(e) => handleSettingChange('username', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-gray-500 focus:border-transparent"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Bio
                </label>
                <textarea
                  value={settings.bio}
                  onChange={(e) => handleSettingChange('bio', e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-gray-500 focus:border-transparent resize-none"
                />
              </div>
            </div>
          </motion.section>

          {/* Privacy Section */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6"
          >
            <div className="flex items-center space-x-3 mb-6">
              <SafeIcon icon={FiLock} className="w-5 h-5 text-gray-600 dark:text-gray-300" />
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                Privacy & Publishing
              </h2>
            </div>
            
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                  Default privacy for new work entries
                </label>
                <div className="space-y-2">
                  {[
                    { value: 'private', label: 'Private', description: 'Only visible to you' },
                    { value: 'public', label: 'Public', description: 'Visible to everyone' }
                  ].map((option) => (
                    <label key={option.value} className="flex items-start space-x-3 cursor-pointer">
                      <input
                        type="radio"
                        name="defaultPrivacy"
                        value={option.value}
                        checked={settings.defaultPrivacy === option.value}
                        onChange={(e) => handleSettingChange('defaultPrivacy', e.target.value)}
                        className="mt-1"
                      />
                      <div>
                        <div className="font-medium text-gray-900 dark:text-white">
                          {option.label}
                        </div>
                        <div className="text-sm text-gray-600 dark:text-gray-300">
                          {option.description}
                        </div>
                      </div>
                    </label>
                  ))}
                </div>
              </div>
              
              <div>
                <label className="flex items-center space-x-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={settings.autoPublish}
                    onChange={(e) => handleSettingChange('autoPublish', e.target.checked)}
                  />
                  <div>
                    <div className="font-medium text-gray-900 dark:text-white">
                      Auto-publish public entries
                    </div>
                    <div className="text-sm text-gray-600 dark:text-gray-300">
                      Automatically make entries public when they meet quality thresholds
                    </div>
                  </div>
                </label>
              </div>
            </div>
          </motion.section>

          {/* Integrations Section */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6"
          >
            <div className="flex items-center space-x-3 mb-6">
              <SafeIcon icon={FiLink} className="w-5 h-5 text-gray-600 dark:text-gray-300" />
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                Integrations
              </h2>
            </div>
            
            <div className="space-y-4">
              {integrations.map((integration, index) => (
                <div key={index} className="flex items-center justify-between p-4 border border-gray-200 dark:border-gray-600 rounded-lg">
                  <div>
                    <h3 className="font-medium text-gray-900 dark:text-white">
                      {integration.name}
                    </h3>
                    {integration.connected && integration.lastSync && (
                      <p className="text-sm text-gray-600 dark:text-gray-300">
                        Last sync: {integration.lastSync}
                      </p>
                    )}
                  </div>
                  <button
                    className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                      integration.connected
                        ? 'bg-red-100 dark:bg-red-900 text-red-700 dark:text-red-300 hover:bg-red-200 dark:hover:bg-red-800'
                        : 'bg-gray-900 dark:bg-white text-white dark:text-gray-900 hover:bg-gray-800 dark:hover:bg-gray-100'
                    }`}
                  >
                    {integration.connected ? 'Disconnect' : 'Connect'}
                  </button>
                </div>
              ))}
            </div>
          </motion.section>

          {/* Notifications Section */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6"
          >
            <div className="flex items-center space-x-3 mb-6">
              <SafeIcon icon={FiGlobe} className="w-5 h-5 text-gray-600 dark:text-gray-300" />
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                Notifications
              </h2>
            </div>
            
            <div className="space-y-4">
              <label className="flex items-center space-x-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={settings.emailNotifications}
                  onChange={(e) => handleSettingChange('emailNotifications', e.target.checked)}
                />
                <div>
                  <div className="font-medium text-gray-900 dark:text-white">
                    Email notifications
                  </div>
                  <div className="text-sm text-gray-600 dark:text-gray-300">
                    Get notified about important updates and activity
                  </div>
                </div>
              </label>
              
              <label className="flex items-center space-x-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={settings.weeklyDigest}
                  onChange={(e) => handleSettingChange('weeklyDigest', e.target.checked)}
                />
                <div>
                  <div className="font-medium text-gray-900 dark:text-white">
                    Weekly digest
                  </div>
                  <div className="text-sm text-gray-600 dark:text-gray-300">
                    Receive a summary of your week's work
                  </div>
                </div>
              </label>
            </div>
          </motion.section>

          {/* Actions */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="flex items-center justify-between pt-6"
          >
            <button
              onClick={handleDeleteAccount}
              className="flex items-center space-x-2 text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 transition-colors"
            >
              <SafeIcon icon={FiTrash2} className="w-4 h-4" />
              <span>Delete account</span>
            </button>
            
            <button
              onClick={handleSave}
              className="flex items-center space-x-2 bg-gray-900 dark:bg-white text-white dark:text-gray-900 px-6 py-3 rounded-lg font-semibold hover:bg-gray-800 dark:hover:bg-gray-100 transition-colors"
            >
              <SafeIcon icon={FiSave} className="w-4 h-4" />
              <span>Save changes</span>
            </button>
          </motion.div>
        </div>
      </div>
    </motion.div>
  );
}

export default SettingsPage;